import { useSessionStore } from '@/entities/session';
import { Logo1Icon } from '@/shared/assets/icons/logo-1';
import { ENV } from '@/shared/lib/env';
import { Typography } from '@/shared/ui';
import { GlobalFacilitySelect } from '@/widgets/global-facility-select';
import {
  PeriodSelector,
  useGlobalPeriodStore,
  useMonthsOnlyRangeSync,
} from '@/widgets/period-selector';
import {
  DATELESS_TABS,
  MONTHS_ONLY_TABS,
  useFacilityTabsStore,
} from '@/widgets/single-facility-tabs';
import { Link, useRouterState } from '@tanstack/react-router';
import type { FC, ReactNode } from 'react';

type HeaderProps = {
  rightAddons?: ReactNode[];
  /**
   * Renders the facility dropdown and date controls. Off by default so the
   * public layout (login, forgot password) shows a bare header.
   */
  withFilters?: boolean;
};

export const Header: FC<HeaderProps> = ({
  rightAddons,
  withFilters = false,
}) => {
  const isDemo = useSessionStore((state) => state.user?.organization?.isDemo);
  const periodSelectorState = useGlobalPeriodStore(
    (state) => state.periodSelectorState
  );
  const changePeriodSelectorState = useGlobalPeriodStore(
    (state) => state.changePeriodSelectorState
  );
  const activeTabId = useFacilityTabsStore((state) => state.activeTabId);
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });

  // Edit Facility is a focused form -- no filters at all.
  const isFacilityEdit = /^\/facilities\/[^/]+\/edit/.test(pathname);
  // Market Intelligence has its own in-page facility and date pickers; showing
  // the global ones too is just confusing (cards #157, #234).
  const isMarketIntel = pathname.startsWith('/market-intel');
  const isFacilitiesPage = /^\/facilities\/?$/.test(pathname);

  const showFacilitySelect = withFilters && !isFacilityEdit && !isMarketIntel;
  const showPeriodSelector = withFilters && !isFacilityEdit && !isMarketIntel;

  // Floor Plan and Info show current state, not a date range.
  const isPeriodDisabled =
    isFacilitiesPage && DATELESS_TABS.includes(activeTabId);

  // Financial metrics are monthly, so that tab picks whole completed months --
  // month pickers, current month blocked, and 1M/6M/1Y re-based onto months.
  const isMonthsOnly =
    isFacilitiesPage && MONTHS_ONLY_TABS.includes(activeTabId);

  useMonthsOnlyRangeSync(isMonthsOnly);

  // The demo is prospect-facing, so it never shows environment chrome — the "DEMO"
  // wordmark below already says where you are. Regular (non-demo) staging keeps its
  // badge so internal users can still tell staging from production at a glance.
  const envBadge = isDemo
    ? null
    : ENV.isDevelopment
      ? {
          label: 'LOCAL',
          dark: 'bg-env-local-dark',
          light: 'text-env-local-light',
        }
      : ENV.isStaging
        ? {
            label: 'STAGING',
            dark: 'bg-env-staging-dark',
            light: 'text-env-staging-light',
          }
        : null;

  return (
    <header className="bg-primary-6 shadow-header sticky top-0 left-0 flex min-h-[90px] w-full items-center px-6">
      <div className="flex flex-1 items-center gap-4">
        <Link to="/facilities">
          <Logo1Icon />
        </Link>
        {envBadge && (
          <div
            className={`w-fit rounded-xs px-2 py-[1px] select-none ${envBadge.dark} ${envBadge.light}`}
          >
            <Typography as="span" variant="footnote-description">
              {envBadge.label}
            </Typography>
          </div>
        )}
        {isDemo && (
          // Takes the leftover width so DEMO centres in the gap between the logo
          // and the filter controls, rather than sitting against the logo.
          <div className="flex flex-1 justify-center">
            <Typography
              variant="h1"
              weight="medium"
              className="text-primary-inverse uppercase"
            >
              DEMO
            </Typography>
          </div>
        )}
      </div>

      <div className="flex items-center gap-6">
        {showFacilitySelect && <GlobalFacilitySelect />}
        {showPeriodSelector && (
          <PeriodSelector
            defaultShortDatePeriod="1m"
            monthsOnly={isMonthsOnly}
            disabled={isPeriodDisabled}
            value={periodSelectorState}
            include={['1m', '6m', '1y', 'from', 'to']}
            onDark
            onChange={changePeriodSelectorState}
          />
        )}
        {rightAddons?.map((addon) => addon)}
      </div>
    </header>
  );
};
